import { useGoodsDetail } from '../../../../../queries/useGoodsDetail';
import { ContentWebView } from '../../ContentWebView';
import {
  Flex,
  IconArrowDown,
  PrimaryButton,
  useScreenSize,
} from '@banhala/ui-kit';
import { WhiteLinearGradient } from '../../WhiteLinearGradient';
import React, { useEffect, useMemo, useRef } from 'react';
import { useLogClickGoodsDetailLoadMore } from '../hooks/useLogClickGoodsDetailLoadMore';
import { BottomProductInfoView } from './BottomProductInfoView';
import {
  retainedState,
  useRetainedState,
} from 'shared/src/hooks/retainedState';

const FOLDED_HEIGHT_MULTIPLIER = 2;
const BOTTOM_GRADIENT_HEIGHT = 156;

// 상품 정보 "펼쳐보기" 관련 상태를 visit 단위로 유지한다. depth 2+ 진입으로 PDP content 가
// unmount→remount 되어도(=같은 visit) 펼친 상태/측정 높이/초기화 여부를 보존해, 펼쳐둔 채
// 돌아왔을 때 접힘으로 리셋되거나 높이가 재측정되며 깜빡이는 것을 막는다. (커버 인덱스
// coverIndexSlot 과 동일한 취지)
const foldedSlot = retainedState(true);
const initSlot = retainedState(false);
// 초기값 0 → 읽을 때 `|| foldedHeight`(screenHeight*2, 런타임 의존) 로 폴백. 웹뷰가 실제
// 높이를 측정하면(>0) 그 값이 retained 되어 remount 시 즉시 올바른 높이로 복원된다.
const unfoldedHeightSlot = retainedState(0);

export const GoodsInfoWebView: React.FC<{
  onChangeHeight: (height: number) => void;
}> = props => {
  const { data: goodsDetail } = useGoodsDetail();
  const { height: screenHeight } = useScreenSize();
  const foldedHeight = screenHeight * FOLDED_HEIGHT_MULTIPLIER;
  const onChangeHeightRef = useRef(props.onChangeHeight);
  onChangeHeightRef.current = props.onChangeHeight;

  const [folded, setFolded] = useRetainedState(foldedSlot);
  const [init, setInit] = useRetainedState(initSlot);
  const [rawUnfoldedHeight, setUnfoldedHeight] =
    useRetainedState(unfoldedHeightSlot);
  const unfoldedHeight = rawUnfoldedHeight || foldedHeight;
  const logClickGoodsDetailLoadMore = useLogClickGoodsDetailLoadMore();

  useEffect(() => {
    if (init) return;
    setInit(true);
    onChangeHeightRef.current(foldedHeight + BOTTOM_GRADIENT_HEIGHT);
  }, [init, foldedHeight, setInit]);

  useEffect(() => {
    if (!folded) {
      onChangeHeightRef.current(unfoldedHeight);
    }
  }, [folded, unfoldedHeight]);

  const html = useMemo(() => {
    if (!goodsDetail) return '';

    const productInfoIframe = goodsDetail.goods.detail_html_parts.filter(
      htmlPart => htmlPart.html_part_type === 'PRODUCT_INFO',
    );
    const html = productInfoIframe[0].contents[0];

    return goodsDetail.goods.detail_html.replace(html, '');
  }, [goodsDetail]);

  return (
    <Flex height={folded ? foldedHeight : unfoldedHeight} overflow={'hidden'}>
      <ContentWebView
        source={{ html }}
        onChangeHeight={setUnfoldedHeight}
        // remount 시 200 → 재측정 붕괴 깜빡임 방지: retained 높이를 초기값으로 주입.
        // (rawUnfoldedHeight 가 0 이면 unfoldedHeight 는 foldedHeight 폴백)
        initialHeight={unfoldedHeight}
        maxHeight={folded ? foldedHeight + BOTTOM_GRADIENT_HEIGHT : undefined}
        Footer={folded ? undefined : <BottomProductInfoView />}
      />
      {folded && (
        <Flex
          height={BOTTOM_GRADIENT_HEIGHT}
          position={'absolute'}
          bottom={0}
          left={0}
          right={0}
          justifyContent={'flex-end'}
        >
          <Flex position={'absolute'} left={0} right={0} bottom={0}>
            <WhiteLinearGradient />
          </Flex>
          <Flex
            padding={{ left: 16, right: 16, bottom: 24 }}
            backgroundColor={'bg_always_white'}
          >
            <PrimaryButton
              variant={'fill'}
              onPress={() => {
                setFolded(false);
                logClickGoodsDetailLoadMore();
              }}
              size={'large'}
              testID={'상품정보펼치기버튼'}
              rightIcon={<IconArrowDown />}
              color={'blue'}
              outline
            >
              상품 정보 펼쳐보기
            </PrimaryButton>
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};
